/**
 * Accept or reject the suggestion the cursor is standing in.
 *
 * AT THE CURSOR RATHER THAN IN A SIDE PANEL. A panel is the right answer for a document under heavy
 * review, where somebody works through forty suggestions in order; this is the right answer for the
 * commoner case, which is reading a procedure and coming across one. The panel can come later
 * without this being wrong.
 *
 * IT NAMES WHO, because "accept this change" is a different decision depending on whether the
 * person proposing it is the process owner or somebody who read it once.
 *
 * NOT SHOWN WHEN THE REVISION IS CLOSED. Accepting is an edit -- it changes the text that gets
 * signed -- so it is gated exactly as typing is, by t1, on the server, on the save that follows.
 */
import React from "react";
import { Box, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import { Check, Close } from "@mui/icons-material";

import { colourFor, resolveSuggestion, suggestionAt } from "./suggestions";
import { Grey5, Grey17 } from "../../theme/styles";

export default function SuggestionBubble({ editor, editable }: { editor: any; editable: boolean }) {
    const [, bump] = React.useReducer((n: number) => n + 1, 0);

    React.useEffect(() => {
        if (!editor) return;
        editor.on("selectionUpdate", bump);
        editor.on("transaction", bump);
        return () => { editor.off("selectionUpdate", bump); editor.off("transaction", bump); };
    }, [editor]);

    if (!editor || !editable) return null;
    const found = suggestionAt(editor);
    if (!found) return null;

    // POSITIONED FROM THE SELECTION'S OWN COORDINATES, so the bubble follows the text through
    // scrolling and reflow rather than being pinned to where the page happened to be.
    let box: { left: number; top: number };
    try {
        const start = editor.view.coordsAtPos(found.from);
        box = { left: start.left, top: start.top };
    } catch {
        return null;                                  // position no longer in the document
    }

    const who = found.mark.attrs.userName || "Someone";
    const colour = colourFor(found.mark.attrs.userId || "");

    return (
        <Paper elevation={4} sx={{
            position: "fixed", left: Math.max(12, box.left), top: Math.max(64, box.top - 46),
            zIndex: 1250, display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.5,
            borderRadius: 2,
        }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: colour }}/>
            <Typography sx={{ fontSize: 12, color: Grey17, fontFamily: "Roboto, Arial, sans-serif" }}>
                {who} {found.kind === "insertion" ? "added" : "deleted"} this
            </Typography>
            <Typography sx={{ fontSize: 11, color: Grey5, mr: 0.5 }}>
                {found.mark.attrs.at ? new Date(found.mark.attrs.at).toLocaleDateString() : ""}
            </Typography>
            <Tooltip title="Accept">
                <IconButton size="small" aria-label="Accept suggestion"
                            onClick={() => resolveSuggestion(editor, found, true)}>
                    <Check sx={{ fontSize: 17, color: "#1E9E5A" }}/>
                </IconButton>
            </Tooltip>
            <Tooltip title="Reject">
                <IconButton size="small" aria-label="Reject suggestion"
                            onClick={() => resolveSuggestion(editor, found, false)}>
                    <Close sx={{ fontSize: 17, color: "#C0392B" }}/>
                </IconButton>
            </Tooltip>
        </Paper>
    );
}
